import * as THREE from 'three/webgpu';
import {
  evaluateCurveAtLengthByEntity,
  getCurveLengthByEntity,
  getEntityPointByEntity,
  getFaceCentroidByEntity,
  intersectEntitiesByEntity,
  projectPointNormalByEntity,
  transformPointFromCoordinateFrameByFrame,
} from '../tool/PointFeatureSolveRuntimeFunctions';
import {readNumber} from '../tool/PointFeatureSolveContextGeometryUtils';
import type {PointFeatureSolveGeometryData} from '../tool/PointFeatureSolveGeometryPayloadTypes';
import {
  ReferenceCoordinateFrame,
  ReferenceDiagnosticSeverity,
  ReferenceEntitySemanticType,
} from '../ReferenceFeatureTypes';
import type {ReferencePointController} from './ReferencePointController';

/**
 * 基准点本地实体引用。
 */
export interface PointEntityReference {
  /** 实体 ID。 */
  entityId: string;
  /** 实体类型。 */
  entityType: ReferenceEntitySemanticType;
  /** 槽位标识。 */
  slot: string;
  /** 装配体导入拓扑稳定引用。 */
  assemblyTopologyRef?: AssemblyTopologySelectionRef | null;

    /** 选择层解析出的拓扑命中快照，用于特征重建后从新结果体恢复代理引用。 */
  topologyPick?: ResolvedTopologyPick | null;

}

/**
 * 基准点放置驱动类型。
 */
export enum PointPlacementDriver {
  /** 通过几何引用求解基准点。 */
  Geometry = 'geometry',
  /** 通过数值 XYZ 坐标直接创建基准点。 */
  NumericXYZ = 'numericXYZ',
}

/**
 * 基准点放置驱动枚举值兼容类型。
 *
 * 说明：
 * 1. cad-core 内部状态统一使用 `PointPlacementDriver` 枚举；
 * 2. 外层 UI 历史上会直接传入同值字符串，公共入口需要先兼容再归一化；
 * 3. 该类型只用于边界入参，不作为持久化状态类型。
 */
export type PointPlacementDriverValue = `${PointPlacementDriver}`;

/**
 * 沿曲线布点分布方式。
 */
export enum PointAlongCurveDistributionType {
  /** 按固定距离布点。 */
  Distance = 'distance',
  /** 按曲线总长百分比布点。 */
  Percentage = 'percentage',
  /** 按曲线长度均匀分布。 */
  EvenlyDistributed = 'evenlyDistributed',
}

/**
 * 沿曲线布点分布方式兼容值。
 */
export type PointAlongCurveDistributionTypeValue = `${PointAlongCurveDistributionType}`;

/**
 * 沿曲线布点起点策略。
 */
export enum PointAlongCurveStartPolicy {
  /** 使用拓扑天然起点。 */
  TopologicalStart = 'topologicalStart',
  /** 使用离拾取位置最近的端点。 */
  PickNearestEndpoint = 'pickNearestEndpoint',
  /** 使用显式指定端点。 */
  ExplicitEndpoint = 'explicitEndpoint',
}

/**
 * 沿曲线布点起点策略兼容值。
 */
export type PointAlongCurveStartPolicyValue = `${PointAlongCurveStartPolicy}`;

/**
 * 曲线方向策略。
 */
export enum PointCurveDirectionPolicy {
  /** 使用曲线天然方向。 */
  Natural = 'natural',
  /** 反转曲线方向。 */
  Reversed = 'reversed',
}

/**
 * 曲线方向策略兼容值。
 */
export type PointCurveDirectionPolicyValue = `${PointCurveDirectionPolicy}`;

/**
 * 沿曲线布点端点包含策略。
 */
export enum PointIncludeEndpointPolicy {
  /** 不包含端点。 */
  None = 'none',
  /** 仅包含起点。 */
  StartOnly = 'startOnly',
  /** 仅包含终点。 */
  EndOnly = 'endOnly',
  /** 同时包含起点和终点。 */
  Both = 'both',
}

/**
 * 沿曲线端点包含策略兼容值。
 */
export type PointIncludeEndpointPolicyValue = `${PointIncludeEndpointPolicy}`;

/**
 * 交点多解拾取策略。
 */
export enum PointPickPolicy {
  /** 默认选择第一个求解结果。 */
  First = 'first',
  /** 选择距离光标最近的求解结果。 */
  NearestToCursor = 'nearestToCursor',
  /** 要求用户手动消歧。 */
  RequireUserDisambiguation = 'requireUserDisambiguation',
}

/**
 * 交点多解拾取策略兼容值。
 */
export type PointPickPolicyValue = `${PointPickPolicy}`;

/**
 * 投影多解处理策略。
 */
export enum PointProjectionAmbiguityPolicy {
  /** 多解时直接失败。 */
  Fail = 'fail',
  /** 多解时自动选择最近结果。 */
  PickNearest = 'pickNearest',
  /** 多解时要求用户消歧。 */
  RequireDisambiguation = 'requireDisambiguation',
}

/**
 * 投影多解处理策略兼容值。
 */
export type PointProjectionAmbiguityPolicyValue = `${PointProjectionAmbiguityPolicy}`;

/**
 * 基准点高亮显示策略。
 */
export enum PointHighlightStylePolicy {
  /** 使用 SolidWorks 风格高亮。 */
  SolidworksLike = 'solidworksLike',
  /** 使用自定义高亮样式。 */
  Custom = 'custom',
}

/**
 * 基准点高亮显示策略兼容值。
 */
export type PointHighlightStylePolicyValue = `${PointHighlightStylePolicy}`;

/**
 * 基准点几何方法枚举。
 */
export enum PointGeometryMethod {
  /** 使用圆、圆弧或圆边的圆心创建基准点。 */
  CircularCenter = 'referencePoint.circularCenter',
  /** 使用面的几何中心创建基准点。 */
  CenterOfFace = 'referencePoint.centerOfFace',
  /** 使用两个几何实体的交点创建基准点。 */
  Intersection = 'referencePoint.intersection',
  /** 将点法向投影到目标面或曲面上。 */
  Projection = 'referencePoint.projection',
  /** 直接复用已有点性实体的位置。 */
  OnPoint = 'referencePoint.onPoint',
  /** 按曲线参数或距离在曲线上布点。 */
  AlongCurve = 'referencePoint.alongCurve',
}

/**
 * 基准点特征参数。
 */
export interface PointFeatureParams {
  /** 数值输入模式下的三维坐标。 */
  xyz?: {
    /** X 坐标。 */
    x: number;
    /** Y 坐标。 */
    y: number;
    /** Z 坐标。 */
    z: number;
  };
  /** 沿曲线布点模式参数。 */
  alongCurve?: {
    /** 分布方式。 */
    distributionType: PointAlongCurveDistributionType | PointAlongCurveDistributionTypeValue;
    /** 生成点数量。 */
    pointCount: number;
    /** 点间距或百分比值。 */
    spacingValue?: number;
    /** 起点选取策略。 */
    startPolicy?: PointAlongCurveStartPolicy | PointAlongCurveStartPolicyValue;
    /** 曲线方向策略。 */
    directionPolicy?: PointCurveDirectionPolicy | PointCurveDirectionPolicyValue;
    /** 是否包含端点。 */
    includeEndpointPolicy?: PointIncludeEndpointPolicy | PointIncludeEndpointPolicyValue;
    /** 曲线求值容差。 */
    curveEvaluationTolerance?: number;
  };
  /** 交点模式参数。 */
  intersection?: {
    /** 多解时的拾取策略。 */
    pickPolicy?: PointPickPolicy | PointPickPolicyValue;
  };
  /** 投影模式参数。 */
  projection?: {
    /** 多解时的歧义处理策略。 */
    ambiguityPolicy?: PointProjectionAmbiguityPolicy | PointProjectionAmbiguityPolicyValue;
  };
}

/**
 * 基准点特征策略。
 */
export interface PointFeaturePolicies {
  /** 模式切换时保留兼容选择。 */
  retainCompatibleSelectionsOnModeSwitch: boolean;
  /** 交点多解处理策略。 */
  intersectionPickPolicy: PointPickPolicy | PointPickPolicyValue;
  /** 沿曲线模式的起点策略。 */
  alongCurveStartPolicy: PointAlongCurveStartPolicy | PointAlongCurveStartPolicyValue;
  /** 投影模式的多解策略。 */
  projectionAmbiguityPolicy: PointProjectionAmbiguityPolicy | PointProjectionAmbiguityPolicyValue;
  /** 高亮显示策略。 */
  highlightStylePolicy: PointHighlightStylePolicy | PointHighlightStylePolicyValue;
  /** 手动锁定模式是否保持到重置。 */
  manualModeOverridePersistsUntilReset: boolean;
}

/**
 * 默认基准点特征策略。
 */
export const DEFAULT_POINT_FEATURE_POLICIES: PointFeaturePolicies = {
  retainCompatibleSelectionsOnModeSwitch: true,
  intersectionPickPolicy: PointPickPolicy.First,
  alongCurveStartPolicy: PointAlongCurveStartPolicy.TopologicalStart,
  projectionAmbiguityPolicy: PointProjectionAmbiguityPolicy.Fail,
  highlightStylePolicy: PointHighlightStylePolicy.SolidworksLike,
  manualModeOverridePersistsUntilReset: true,
};

/**
 * 基准点特征定义。
 */
export interface PointFeatureDefinition {
  /** 特征 ID。 */
  id: string;
  /** 基准点驱动类型。 */
  placementDriver: PointPlacementDriver;
  /** 几何构造方法。 */
  geometryMethod: PointGeometryMethod | null;
  /** 参考实体集合。 */
  references: PointEntityReference[];
  /** 特征参数。 */
  params: PointFeatureParams;
  /** 交互与求解策略。 */
  policies: PointFeaturePolicies;
  /** 构造上下文。 */
  context: {
    /** 文档 ID。 */
    documentId: string;
    /** 可选编辑上下文路径。 */
    editContextPath?: string[];
    /** 坐标系类型。 */
    coordinateFrame: ReferenceCoordinateFrame;
    /** 单位制标识。 */
    unitSystem: string;
  };
}

/**
 * 基准点求解状态。
 */
export enum PointFeatureStatus {
  /** 已成功求解。 */
  Ready = 'ready',
  /** 信息不完整，需要补充输入。 */
  Incomplete = 'incomplete',
  /** 输入非法，当前组合不可求解。 */
  Invalid = 'invalid',
  /** 存在多解，需要用户消歧。 */
  Ambiguous = 'ambiguous',
  /** 求解过程中发生错误。 */
  Error = 'error',
}

/**
 * 基准点诊断。
 */
export interface PointFeatureDiagnostic {
  /** 诊断码。 */
  code: string;
  /** 诊断消息。 */
  message: string;
  /** 附加上下文。 */
  details?: unknown;
  /** 严重级别。 */
  severity: ReferenceDiagnosticSeverity;
}

/**
 * 基准点求解结果。
 */
export interface PointFeatureResult {
  /** 求解得到的点集合。 */
  points: THREE.Vector3[];
  /** 当前求解状态。 */
  status: PointFeatureStatus;
  /** 求解诊断列表。 */
  diagnostics: PointFeatureDiagnostic[];
}

/**
 * 已解析实体类型。
 */
export type ResolvedEntityType = ReferenceEntitySemanticType;

/**
 * 已解析几何实体。
 */
export interface ResolvedGeometryEntity {
  /** 实体 ID。 */
  id: string;
  /** 实体几何类型。 */
  type: ResolvedEntityType;
  /** 解析后的几何数据。 */
  data: PointFeatureSolveGeometryData;
}

/**
 * 交点求解选项。
 */
export interface SolveIntersectionOptions {
  /** 多交点时的拾取策略。 */
  pickPolicy: PointPickPolicy | PointPickPolicyValue;
}

/**
 * 投影求解选项。
 */
export interface SolveProjectionOptions {
  /** 多投影结果时的处理策略。 */
  ambiguityPolicy: PointProjectionAmbiguityPolicy | PointProjectionAmbiguityPolicyValue;
}

/**
 * 构造求解结果。
 */
export function makeResult(
  status: PointFeatureStatus,
  points: THREE.Vector3[] = [],
  diagnostics: PointFeatureDiagnostic[] = [],
): PointFeatureResult {
  // 统一封装结果对象，避免各子求解器重复拼装返回结构。
  return { status, points, diagnostics };
}

/**
 * 构造诊断。
 */
export function diag(
  code: string,
  message: string,
  severity: PointFeatureDiagnostic['severity'] = ReferenceDiagnosticSeverity.Error,
  details?: unknown,
): PointFeatureDiagnostic {
  // 所有诊断都经过同一入口生成，便于后续统一扩展诊断字段。
  return { code, message, severity, details };
}

/**
 * 基准点统一求解器。
 *
 * 设计说明：
 * 1. 将原 `features` 目录中的定义、策略、结果、工具函数和子求解器统一内聚到本类与本文件；
 * 2. 控制器在构造时持有求解器实例，预览与提交重建均走实例求解；
 * 3. 对外保留求解相关类型导出，避免上层继续依赖已删除的子目录。
 */
export class ReferencePointSolver {
  private readonly host: ReferencePointController;

  /**
   * 创建基准点统一求解器。
   *
   * @param host 求解宿主。
   */
  public constructor(host: ReferencePointController) {
    this.host = host;
  }

  /**
   * 求解基准点特征。
   */
  public solve(definition: PointFeatureDefinition): PointFeatureResult {
    const policies = { ...DEFAULT_POINT_FEATURE_POLICIES, ...definition.policies };

    // 数值坐标模式不依赖几何选择，可直接走专用求解分支。
    if (definition.placementDriver === PointPlacementDriver.NumericXYZ) {
      return this.solveNumericXYZ(definition);
    }

    if (definition.placementDriver !== PointPlacementDriver.Geometry) {
      return makeResult(PointFeatureStatus.Invalid, [], [diag('POINT_PLACEMENT_DRIVER_INVALID', '未知的 placementDriver')]);
    }

    if (!definition.geometryMethod) {
      return makeResult(PointFeatureStatus.Incomplete, [], [diag('POINT_GEOMETRY_METHOD_REQUIRED', 'geometry 模式下必须选择几何构造方法')]);
    }

    const { resolved, diagnostics } = this.resolveReferences(definition);

    // 按当前几何方法分派到对应子求解逻辑，保证每种构造方式职责独立。
    let result: PointFeatureResult;
    switch (definition.geometryMethod) {
      case PointGeometryMethod.CircularCenter:
        result = this.solveCircularCenter(resolved);
        break;
      case PointGeometryMethod.CenterOfFace:
        result = this.solveCenterOfFace(resolved);
        break;
      case PointGeometryMethod.Intersection:
        result = this.solveIntersection(resolved, {
          pickPolicy: definition.params.intersection?.pickPolicy ?? policies.intersectionPickPolicy,
        });
        break;
      case PointGeometryMethod.Projection:
        result = this.solveProjection(resolved, {
          ambiguityPolicy: definition.params.projection?.ambiguityPolicy ?? policies.projectionAmbiguityPolicy,
        });
        break;
      case PointGeometryMethod.OnPoint:
        result = this.solveOnPoint(resolved);
        break;
      case PointGeometryMethod.AlongCurve:
        result = this.solveAlongCurve(resolved, definition.params.alongCurve);
        break;
      default:
        result = makeResult(
          PointFeatureStatus.Invalid,
          [],
          [diag('POINT_GEOMETRY_METHOD_UNSUPPORTED', `不支持的方法: ${String(definition.geometryMethod)}`)],
        );
        break;
    }

    if (diagnostics.length === 0) {
      return result;
    }

    // 引用解析阶段的告警需要与子求解结果合并，避免丢失上游问题上下文。
    return {
      ...result,
      diagnostics: [...diagnostics, ...result.diagnostics],
      status: result.status === PointFeatureStatus.Ready
        && diagnostics.some((item) => item.severity === ReferenceDiagnosticSeverity.Warning)
        ? PointFeatureStatus.Incomplete
        : result.status,
    };
  }

  /**
   * 解析全部几何引用。
   */
  private resolveReferences(
    definition: PointFeatureDefinition,
  ): { resolved: ResolvedGeometryEntity[]; diagnostics: PointFeatureResult['diagnostics'] } {
    const resolved: ResolvedGeometryEntity[] = [];
    const diagnostics: PointFeatureResult['diagnostics'] = [];

    // 逐条解析引用并保留失败诊断，保证预览阶段可以给出完整缺失信息。
    definition.references.forEach((reference) => {
      const entity = this.host.resolveReference(reference);
      if (!entity) {
        diagnostics.push(
          diag(
            'POINT_REFERENCE_UNRESOLVED',
            `引用解析失败: slot=${reference.slot}, entityId=${reference.entityId}`,
            ReferenceDiagnosticSeverity.Warning,
            reference,
          ),
        );
        return;
      }
      resolved.push(entity);
    });

    return { resolved, diagnostics };
  }

  /**
   * 圆心求解。
   */
  private solveCircularCenter(resolvedReferences: ResolvedGeometryEntity[]): PointFeatureResult {
    const allowed = new Set<ResolvedEntityType>([ReferenceEntitySemanticType.FeatureCurve]);
    if (resolvedReferences.length < 1) {
      return makeResult(PointFeatureStatus.Incomplete, [], [diag('POINT_CIRCULAR_CENTER_NEED_REFERENCE', '请先选择圆/圆弧/圆边')]);
    }
    const entity = resolvedReferences[0];
    if (!allowed.has(entity.type)) {
      return makeResult(PointFeatureStatus.Invalid, [], [diag('POINT_CIRCULAR_CENTER_INVALID_TYPE', 'Circular Center 仅支持圆、圆弧、圆形边')]);
    }
    const center = entity.data.center?.clone() ?? null;
    if (!center) {
      return makeResult(PointFeatureStatus.Error, [], [diag('POINT_CIRCULAR_CENTER_NO_CENTER', '选中实体缺少解析圆心')]);
    }
    return makeResult(PointFeatureStatus.Ready, [center]);
  }

  /**
   * 面中心求解。
   */
  private solveCenterOfFace(resolvedReferences: ResolvedGeometryEntity[]): PointFeatureResult {
    if (resolvedReferences.length < 1) {
      return makeResult(PointFeatureStatus.Incomplete, [], [diag('POINT_FACE_CENTER_NEED_FACE', '请先选择面')]);
    }
    const face = resolvedReferences[0];

      if (face.type !== ReferenceEntitySemanticType.FeatureFace && face.type !== ReferenceEntitySemanticType.FeatureSurface) {
      return makeResult(PointFeatureStatus.Invalid, [], [diag('POINT_FACE_CENTER_INVALID_TYPE', 'Center Of Face 仅支持面/曲面实体')]);
    }

      const centroid = getFaceCentroidByEntity(face);
    if (!centroid) {
      return makeResult(PointFeatureStatus.Error, [], [diag('POINT_FACE_CENTER_FAILED', '无法计算面质心')]);
    }
    return makeResult(PointFeatureStatus.Ready, [centroid]);
  }

  /**
   * 交点求解。
   */
  private solveIntersection(
    resolvedReferences: ResolvedGeometryEntity[],
    options: SolveIntersectionOptions,
  ): PointFeatureResult {
    if (resolvedReferences.length < 2) {
      return makeResult(PointFeatureStatus.Incomplete, [], [diag('POINT_INTERSECTION_NEED_TWO', 'Intersection 需要选择两个实体')]);
    }

    const [entityA, entityB] = resolvedReferences;
    const points = intersectEntitiesByEntity(entityA, entityB);
    if (points.length === 0) {
      return makeResult(PointFeatureStatus.Invalid, [], [diag('POINT_INTERSECTION_NO_SOLUTION', '所选实体无有效交点')]);
    }

    // 多交点场景必须遵循策略处理，否则会产生与 SolidWorks 不一致的默认选点结果。
    if (points.length > 1) {
      if (options.pickPolicy === PointPickPolicy.RequireUserDisambiguation) {
        return makeResult(
          PointFeatureStatus.Ambiguous,
          points,
          [diag('POINT_INTERSECTION_AMBIGUOUS', '存在多个交点，请手动消歧', ReferenceDiagnosticSeverity.Warning, { pointCount: points.length })],
        );
      }
      return makeResult(
        PointFeatureStatus.Ready,
        [points[0]],
        [diag('POINT_INTERSECTION_PICKED_FIRST', '存在多个交点，已按策略选取第一个', ReferenceDiagnosticSeverity.Warning)],
      );
    }

    return makeResult(PointFeatureStatus.Ready, [points[0]]);
  }

  /**
   * 投影求解。
   */
  private solveProjection(
    resolvedReferences: ResolvedGeometryEntity[],
    options: SolveProjectionOptions,
  ): PointFeatureResult {
    const targetTypes = new Set<ResolvedEntityType>([
      ReferenceEntitySemanticType.DatumPlane,
      ReferenceEntitySemanticType.FeatureFace,
      ReferenceEntitySemanticType.FeatureSurface,
    ]);
    if (resolvedReferences.length < 2) {
      return makeResult(PointFeatureStatus.Incomplete, [], [diag('POINT_PROJECTION_NEED_SOURCE_TARGET', 'Projection 需要先选 source 再选 target')]);
    }

    const source = resolvedReferences[0];
    const target = resolvedReferences[1];
    if (!targetTypes.has(target.type)) {
      return makeResult(PointFeatureStatus.Invalid, [], [diag('POINT_PROJECTION_INVALID_TARGET', 'Projection target 必须是 plane/face/surface')]);
    }

    const sourcePoint = getEntityPointByEntity(source);
    if (!sourcePoint) {
      return makeResult(PointFeatureStatus.Invalid, [], [diag('POINT_PROJECTION_INVALID_SOURCE', 'Projection source 必须是点性实体')]);
    }

    const points = projectPointNormalByEntity(sourcePoint, target);
    if (points.length === 0) {
      return makeResult(PointFeatureStatus.Invalid, [], [diag('POINT_PROJECTION_NO_SOLUTION', '法向投影无解')]);
    }

    // 投影存在多个候选时，需要显式按策略处理，不允许隐式吞掉歧义。
    if (points.length > 1) {
      if (options.ambiguityPolicy === PointProjectionAmbiguityPolicy.RequireDisambiguation) {
        return makeResult(
          PointFeatureStatus.Ambiguous,
          points,
          [diag('POINT_PROJECTION_AMBIGUOUS', '投影结果不唯一，请手动消歧', ReferenceDiagnosticSeverity.Warning)],
        );
      }
      if (options.ambiguityPolicy === PointProjectionAmbiguityPolicy.Fail) {
        return makeResult(PointFeatureStatus.Invalid, [], [diag('POINT_PROJECTION_AMBIGUOUS_FAIL', '投影结果不唯一，当前策略禁止自动选取')]);
      }
      return makeResult(
        PointFeatureStatus.Ready,
        [points[0]],
        [diag('POINT_PROJECTION_PICKED_NEAREST', '投影结果不唯一，已按策略选取一个候选', ReferenceDiagnosticSeverity.Warning)],
      );
    }

    return makeResult(PointFeatureStatus.Ready, [points[0]]);
  }

  /**
   * 在点上求解。
   */
  private solveOnPoint(resolvedReferences: ResolvedGeometryEntity[]): PointFeatureResult {
    if (resolvedReferences.length < 1) {
      return makeResult(PointFeatureStatus.Incomplete, [], [diag('POINT_ON_POINT_NEED_REFERENCE', '请先选择点性实体')]);
    }
    const point = getEntityPointByEntity(resolvedReferences[0]);
    if (!point) {
      return makeResult(PointFeatureStatus.Invalid, [], [diag('POINT_ON_POINT_INVALID_TYPE', 'On Point 仅支持特征点或基准点')]);
    }
    return makeResult(PointFeatureStatus.Ready, [point]);
  }

  /**
   * 沿曲线求解。
   */
  private solveAlongCurve(
    resolvedReferences: ResolvedGeometryEntity[],
    alongCurve: PointFeatureParams['alongCurve'],
  ): PointFeatureResult {
    if (resolvedReferences.length < 1) {
      return makeResult(PointFeatureStatus.Incomplete, [], [diag('POINT_ALONG_CURVE_NEED_CURVE', 'Along curve 需要选择一条曲线')]);
    }
    if (!alongCurve) {
      return makeResult(PointFeatureStatus.Invalid, [], [diag('POINT_ALONG_CURVE_PARAMS_MISSING', 'Along curve 参数缺失')]);
    }

    const curve = resolvedReferences[0];
    const curveLength = getCurveLengthByEntity(curve);
    if (!curveLength || curveLength <= 0) {
      return makeResult(PointFeatureStatus.Invalid, [], [diag('POINT_ALONG_CURVE_INVALID_CURVE', '曲线长度无效或退化')]);
    }

    const pointCount = Math.max(0, Math.floor(alongCurve.pointCount));
    if (pointCount <= 0) {
      return makeResult(PointFeatureStatus.Invalid, [], [diag('POINT_ALONG_CURVE_INVALID_COUNT', '参考点数量必须大于 0')]);
    }

    const distributionType = alongCurve.distributionType;
    const includeEndpointPolicy = alongCurve.includeEndpointPolicy ?? PointIncludeEndpointPolicy.None;
    const direction = alongCurve.directionPolicy ?? PointCurveDirectionPolicy.Natural;
    const sign = direction === PointCurveDirectionPolicy.Reversed ? -1 : 1;
    let distances: number[] = [];

    if (distributionType === PointAlongCurveDistributionType.Distance) {
      const spacing = Number(alongCurve.spacingValue ?? Number.NaN);
      if (!Number.isFinite(spacing) || spacing <= 0) {
        return makeResult(PointFeatureStatus.Invalid, [], [diag('POINT_ALONG_CURVE_INVALID_SPACING', 'Distance 模式下 spacingValue 必须大于 0')]);
      }
      distances = Array.from({ length: pointCount }, (_, index) => spacing * (index + 1));
    } else if (distributionType === PointAlongCurveDistributionType.Percentage) {
      const percentage = Number(alongCurve.spacingValue ?? Number.NaN);
      if (!Number.isFinite(percentage) || percentage <= 0) {
        return makeResult(PointFeatureStatus.Invalid, [], [diag('POINT_ALONG_CURVE_INVALID_PERCENTAGE', 'Percentage 模式下 spacingValue 必须大于 0')]);
      }
      const spacing = curveLength * (percentage / 100);
      distances = Array.from({ length: pointCount }, (_, index) => spacing * (index + 1));
    } else {
      const ratios = this.computeEvenRatios(pointCount, includeEndpointPolicy);
      distances = ratios.map((ratio) => ratio * curveLength);
    }

    const maxDistance = Math.max(...distances);
    if (maxDistance > curveLength + 1e-9) {
      return makeResult(PointFeatureStatus.Invalid, [], [diag('POINT_ALONG_CURVE_OUT_OF_RANGE', '参数超出曲线可用长度，无法按指定数量生成点')]);
    }

    const points = distances
      .map((distance) => (sign > 0 ? distance : curveLength - distance))
      .map((distance) => evaluateCurveAtLengthByEntity(curve, distance))
      .filter((point): point is THREE.Vector3 => point instanceof THREE.Vector3);

    if (points.length !== pointCount) {
      return makeResult(PointFeatureStatus.Error, points, [diag('POINT_ALONG_CURVE_EVALUATION_FAILED', '部分点位求值失败')]);
    }

    return makeResult(PointFeatureStatus.Ready, points);
  }

  /**
   * 数值 XYZ 求解。
   */
  private solveNumericXYZ(definition: PointFeatureDefinition): PointFeatureResult {
    const xyz = definition.params.xyz;
    if (!xyz) {
      return makeResult(PointFeatureStatus.Incomplete, [], [diag('POINT_NUMERIC_XYZ_MISSING', '请先输入 X/Y/Z')]);
    }

    const x = readNumber(xyz.x);
    const y = readNumber(xyz.y);
    const z = readNumber(xyz.z);
    if (x === null || y === null || z === null) {
      return makeResult(PointFeatureStatus.Invalid, [], [diag('POINT_NUMERIC_XYZ_INVALID', 'X/Y/Z 必须为有效数值')]);
    }

    const worldPoint = transformPointFromCoordinateFrameByFrame(
      new THREE.Vector3(x, y, z),
      definition.context.coordinateFrame,
    );
    return makeResult(PointFeatureStatus.Ready, [worldPoint]);
  }

  /**
   * 计算均匀分布比例。
   */
  private computeEvenRatios(
    pointCount: number,
    includeEndpointPolicy: PointIncludeEndpointPolicy | PointIncludeEndpointPolicyValue,
  ): number[] {
    if (pointCount <= 0) {
      return [];
    }
    if (includeEndpointPolicy === PointIncludeEndpointPolicy.Both) {
      if (pointCount === 1) {
        return [0];
      }
      return Array.from({ length: pointCount }, (_, index) => index / (pointCount - 1));
    }
    if (includeEndpointPolicy === PointIncludeEndpointPolicy.StartOnly) {
      if (pointCount === 1) {
        return [0];
      }
      return Array.from({ length: pointCount }, (_, index) => index / pointCount);
    }
    if (includeEndpointPolicy === PointIncludeEndpointPolicy.EndOnly) {
      return Array.from({ length: pointCount }, (_, index) => (index + 1) / pointCount);
    }
    return Array.from({ length: pointCount }, (_, index) => (index + 1) / (pointCount + 1));
  }
}
